import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CollectionImage } from '~/components/collections';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import {
	CaseItemRarity,
	getRandomInt,
	WalletType,
	walletTypeCurrency,
} from '~/shared';
import {
	setOpenCaseResult,
	setShopOpenCaseVisible,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	setActiveCase,
	setIsOpeningCase,
} from '../../redux/slices/mainMenuSlice';
import { formatTime } from '../../utils';
import { calcOldPrice, CaseItemColors } from '../CaseItem';
import { CountPicker } from '../CountPicker';
import { ShopOpenCaseItem } from '../ShopOpenCaseItem';
import { ShopOpenCaseScroll } from '../ShopOpenCaseScroll';
import {
	StyledBackButtonContainer,
	StyledBadgesContainer,
	StyledButtonContainer,
	StyledButtonContent,
	StyledCaseChangeButton,
	StyledCaseChangeContainerLeft,
	StyledCaseChangeContainerRight,
	StyledCaseContent,
	StyledCaseImageContainer,
	StyledCenterContainer,
	StyledCenterDecSvg,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledDotsContainer,
	StyledInfoCase,
	StyledItemsGrid,
	StyledLeftContainer,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledPriceTags,
	StyledRightContainer,
	StyledStockContainer,
	StyledTag,
	StyledTimerContainer,
	StyledTitleTag,
	StyledTopSection,
	StyledTopShadow,
} from './ShopOpenCase.styled';

const ShopOpenCase = () => {
	const dispatch = useLayoutDispatch();
	const activeCase = useLayoutSelector((state) => state.mainMenu.activeCase);
	const cases = useObservable(rage.cases.getLootboxes());
	const isOpeningCase = useLayoutSelector(
		(state) => state.mainMenu.isOpeningCase,
	);
	const openCaseResult = useLayoutSelector(
		(state) => state.mainMenu.openCaseResult,
	);
	const containerRef = useRef<HTMLDivElement>(null);
	const [discountTime, setDiscountTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [count, setCount] = useState(1);
	const caseDisabled = false;
	const [animationDirection, setAnimationDirection] = useState<
		'left' | 'right'
	>('right');

	useEffect(() => {
		setCount(1);
	}, [activeCase?.id]);

	const donateOwnedItems = useObservable(
		rage.inventoryDonate.getDonateOwnedItems(),
	);
	const caseDonateId = activeCase?.donateItemId ?? activeCase?.id;
	const isOwned = useMemo(
		() => donateOwnedItems.some((item) => item.id === caseDonateId),
		[donateOwnedItems, caseDonateId],
	);
	const ownedCount = useMemo(
		() => donateOwnedItems.find((item) => item.id === caseDonateId)?.count || 0,
		[donateOwnedItems, caseDonateId],
	);

	const totalPrice = useMemo(() => {
		return activeCase?.price ? activeCase?.price * count : 0;
	}, [activeCase, count]);

	const currentCaseIndex = useMemo(() => {
		if (!activeCase?.id || !cases.length) return 0;
		return cases.findIndex((caseItem) => caseItem.id === activeCase.id);
	}, [activeCase?.id, cases]);

	const { prevCase, nextCase } = useMemo(() => {
		if (!cases.length) return { prevCase: null, nextCase: null };

		const prevIndex =
			currentCaseIndex > 0 ? currentCaseIndex - 1 : cases.length - 1;
		const nextIndex =
			currentCaseIndex < cases.length - 1 ? currentCaseIndex + 1 : 0;

		return {
			prevCase: cases[prevIndex] || null,
			nextCase: cases[nextIndex] || null,
		};
	}, [cases, currentCaseIndex]);

	const currencyIcon = useWalletIcon(
		walletTypeCurrency[activeCase?.walletType ?? WalletType.Donate],
		true,
	);

	const [direction1, direction2] = useMemo(
		() => [
			getRandomInt(0, 1) === 0 ? 360 : -360,
			getRandomInt(0, 1) === 0 ? 360 : -360,
		],
		[],
	);
	const [dur1, dur2] = useMemo(
		() => [getRandomInt(20, 40), getRandomInt(25, 45)],
		[],
	);

	const tagsConfig = useMemo(() => {
		return activeCase?.tags?.map((tag) => rage.cases.getCaseTagById(tag));
	}, [activeCase?.tags]);

	const processedItems = useMemo(() => {
		if (!activeCase?.id) return [];

		const groupedItems = rage.cases.getGroupedLootboxItems(activeCase.id);

		return groupedItems.map((item) => {
			const imageIds = item.ids
				.map((donateItemId) => rage.cases.getRewardImageId(donateItemId))
				.filter((imageId): imageId is string => Boolean(imageId));

			const firstId = item.ids[0];

			return {
				type: item.type,
				ids: imageIds.length > 0 ? imageIds : item.ids,
				color: CaseItemColors[item?.rarity || CaseItemRarity.COMMON],
				name: rage.cases.getRewardNameById(firstId),
				rarity: item.rarity,
			};
		});
	}, [activeCase?.id]);

	const countTime = useCallback(() => {
		if (activeCase?.discountTimestampEnd) {
			const { time, unit } = formatTime(activeCase?.discountTimestampEnd);
			if (!unit) {
				setDiscountTime(time);
			} else {
				setDiscountTime(`${time} ${unit}`);
			}
		}

		if (activeCase?.enabledTimestampEnd) {
			const { time, unit } = formatTime(activeCase?.enabledTimestampEnd);
			if (!unit) {
				setEndTime(time);
			} else {
				setEndTime(`${time} ${unit}`);
			}
		}
	}, [activeCase]);

	const handleOpen = useCallback(async () => {
		if (!activeCase?.id) {
			return;
		}

		if (containerRef.current) {
			containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}

		const rewardId = await rage.cases.openLootbox(activeCase?.id);
		dispatch(setOpenCaseResult(rewardId));
		dispatch(setIsOpeningCase(true));
	}, [activeCase]);

	const handlePrevCase = useCallback(async () => {
		if (!prevCase) return;

		setAnimationDirection('left');
		try {
			const caseWithItems = rage.cases.getLootboxById(prevCase.id);
			const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
				caseWithItems as any;
			dispatch(setActiveCase(caseWithoutConfig));
		} catch (error) {
			console.error('Ошибка при загрузке предыдущего кейса:', error);
		}
	}, [prevCase, dispatch]);

	const handleNextCase = useCallback(async () => {
		if (!nextCase) return;

		setAnimationDirection('right');
		const caseWithItems = rage.cases.getLootboxById(nextCase.id);
		const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
			caseWithItems as any;
		dispatch(setActiveCase(caseWithoutConfig));
	}, [nextCase, dispatch]);

	const handleBuy = useCallback(async () => {
		if (!activeCase?.id) {
			return;
		}

		if (isOwned) {
			handleOpen().catch((err) => rage.notifications.showException(err));
			return;
		}

		await rage.cases
			.buyLootbox(activeCase?.id, count)
			.catch((err) => rage.notifications.showException(err));
	}, [activeCase, count, isOwned, handleOpen, dispatch]);

	useEffectOnce(() => {
		countTime();
		const interval = setInterval(countTime, 1000);
		return () => clearInterval(interval);
	});

	return (
		<StyledContainer ref={containerRef} $scroll={!isOpeningCase}>
			<StyledTopShadow
				style={{
					backgroundColor: activeCase?.backgroundColor || '#ffffff',
					opacity: isOpeningCase ? 0 : 1,
				}}
			/>
			<Divider
				$direction="H"
				color="#ffffff"
				style={{
					opacity: 0.1,
					position: 'absolute',
					top: 0,
					left: 50,
					right: 50,
					zIndex: 1,
					width: 'auto',
				}}
			/>
			<StyledTopSection $isOpening={isOpeningCase}>
				<StyledDotsContainer $visible={!isOpeningCase} />
				<SwitchTransition>
					<CSSTransition
						key={isOpeningCase ? 'opening' : 'info'}
						timeout={400}
						classNames="case-opening"
						addEndListener={(node, done) =>
							node.addEventListener('transitionend', done, false)
						}
					>
						{isOpeningCase ? (
							<ShopOpenCaseScroll rewardId={openCaseResult} />
						) : (
							<StyledInfoCase>
								<StyledBackButtonContainer>
									<Button
										color={ButtonColors.Default}
										height={ButtonHeight.Height40}
										borderRadius={ButtonBorderRadius.Radius5}
										style={{ width: 170 }}
										onClick={() => {
											dispatch(setShopOpenCaseVisible(false));
											playSound(Sound.ClickButton);
										}}
									>
										<StyledButtonContent style={{ gap: 5 }}>
											<img src={arrowLeft} style={{ width: 14, height: 14 }} />
											<Text>Вернуться назад</Text>
										</StyledButtonContent>
									</Button>
								</StyledBackButtonContainer>
								<StyledCaseChangeContainerLeft>
									<StyledCaseChangeButton
										onClick={() => {
											handlePrevCase();
											playSound(Sound.ClickButton);
										}}
										onMouseEnter={() => playSound(Sound.LinkCursor)}
									>
										<img src={arrowLeft} />
									</StyledCaseChangeButton>
									<Text>
										{prevCase?.name.split(' ').map((word) => (
											<Fragment key={word}>
												{word}
												<br />
											</Fragment>
										)) || 'Предыдущий кейс'}
									</Text>
								</StyledCaseChangeContainerLeft>
								<StyledCaseChangeContainerRight>
									<Text style={{ textAlign: 'right' }}>
										{nextCase?.name.split(' ').map((word) => (
											<Fragment key={word}>
												{word}
												<br />
											</Fragment>
										)) || 'Следующий кейс'}
									</Text>
									<StyledCaseChangeButton
										onClick={() => {
											handleNextCase();
											playSound(Sound.ClickButton);
										}}
										onMouseEnter={() => playSound(Sound.LinkCursor)}
									>
										<img src={arrowRight} />
									</StyledCaseChangeButton>
								</StyledCaseChangeContainerRight>
								<SwitchTransition>
									<CSSTransition
										key={activeCase?.id || 'empty'}
										timeout={400}
										classNames={`case-${animationDirection}`}
										addEndListener={(node, done) =>
											node.addEventListener('transitionend', done, false)
										}
									>
										<StyledCaseContent>
											<StyledLeftContainer>
												<StyledBadgesContainer>
													{tagsConfig?.map((tag) => (
														<StyledTag
															$bgColorNormal={tag?.bgColorNormal ?? ''}
															$textColorNormal={tag?.textColorNormal ?? ''}
															key={tag?.id}
														>
															<Text>{tag?.text}</Text>
														</StyledTag>
													))}
													{discountTime && activeCase?.discount ? (
														<StyledDiscountTimerContainer>
															<Text>{activeCase?.discount * 100}%</Text>
															<div className={'divider'} />
															<Text>{discountTime}</Text>
														</StyledDiscountTimerContainer>
													) : (
														<>
															{endTime && (
																<StyledTimerContainer>
																	<Text>{endTime}</Text>
																</StyledTimerContainer>
															)}
															{activeCase?.discount ? (
																<StyledDiscountContainer>
																	<Text>{activeCase?.discount * 100}%</Text>
																</StyledDiscountContainer>
															) : null}
														</>
													)}
												</StyledBadgesContainer>
												<Text
													style={{
														fontSize: 20,
														fontWeight: 700,
														color: '#ffffff',
													}}
												>
													{activeCase?.name}
												</Text>
												<Text
													style={{
														fontSize: 16,
														fontWeight: 400,
														color: '#ffffff',
														opacity: 0.5,
													}}
												>
													Встречайте новый лимитированный кейс - HUMAN 333!
													<br />
													<br />
													В этом кейсы вы сможете получить редикие награды,
													такие как автомобили, уникальную одежду, акссесуары и
													другие полезные предметы.
													<br />
													<br />
													Успевай забрать свою награду!
												</Text>
											</StyledLeftContainer>
											<StyledCenterContainer>
												<StyledCenterDecSvg
													width="440"
													height="440"
													viewBox="0 0 440 440"
													fill="none"
												>
													<circle
														opacity="0.25"
														cx="225"
														cy="220"
														r="169.5"
														stroke={activeCase?.backgroundColor || '#FFFFFF'}
													/>
													<circle
														opacity="0.1"
														cx="225"
														cy="220"
														r="209.5"
														stroke={activeCase?.backgroundColor || '#FFFFFF'}
													/>

													<g>
														<circle
															cx="434.5"
															cy="221"
															r="5"
															fill={activeCase?.backgroundColor || '#FFFFFF'}
														/>
														<animateTransform
															attributeName="transform"
															attributeType="XML"
															type="rotate"
															from={`0 225 221`}
															to={`${direction1} 225 221`}
															dur={`${dur1}s`}
															begin={`${0}s`}
															repeatCount="indefinite"
														/>
													</g>
													<g>
														<circle
															cx="225"
															cy="50.5"
															r="5"
															fill={activeCase?.backgroundColor || '#FFFFFF'}
														/>
														<animateTransform
															attributeName="transform"
															attributeType="XML"
															type="rotate"
															from={`0 225 220`}
															to={`${direction2} 225 220`}
															dur={`${dur2}s`}
															begin={`${0}s`}
															repeatCount="indefinite"
														/>
													</g>
												</StyledCenterDecSvg>
												<StyledCaseImageContainer>
													<CollectionImage
														collection={'cases'}
														id={activeCase?.id ?? ''}
														size={480}
													/>
												</StyledCaseImageContainer>
											</StyledCenterContainer>
											<StyledRightContainer>
												<StyledStockContainer>
													<StyledTitleTag>
														<Text>{isOwned ? 'В наличии' : 'Количество'}</Text>
													</StyledTitleTag>
													<CountPicker
														value={isOwned ? ownedCount : count}
														onChange={setCount}
														disabled={isOwned}
														min={1}
														max={99}
														placeholder="шт."
													/>
												</StyledStockContainer>
												<StyledPriceTags>
													<StyledTitleTag>
														<Text>Стоимость</Text>
													</StyledTitleTag>
													{activeCase?.discount && (
														<StyledDiscountContainer>
															<Text>{activeCase?.discount * 100}%</Text>
														</StyledDiscountContainer>
													)}
												</StyledPriceTags>
												<StyledPriceContainer>
													<StyledPrice>
														<CurrencyIcon icon={currencyIcon} size={30} />
														<Text>
															{activeCase?.price?.toLocaleString('ru-RU')}
														</Text>
													</StyledPrice>
													{activeCase?.discount && (
														<StyledOldPrice>
															<CurrencyIcon icon={currencyIcon} size={'lg'} />
															<Text>
																{calcOldPrice(
																	activeCase?.price || 0,
																	activeCase?.discount,
																)?.toLocaleString('ru-RU')}
															</Text>
															<div className={'line'} />
														</StyledOldPrice>
													)}
												</StyledPriceContainer>
												<StyledButtonContainer>
													<Button
														color={ButtonColors.Buy}
														height={ButtonHeight.Height50}
														onClick={() => {
															handleBuy();
															playSound(Sound.ClickButton);
														}}
														disabled={caseDisabled}
													>
														<StyledButtonContent>
															<Text>
																{caseDisabled
																	? 'Кейс недоступен'
																	: isOwned
																	? 'Открыть кейс'
																	: 'Купить за '}
															</Text>
															{!caseDisabled && !isOwned && (
																<Fragment>
																	<CurrencyIcon
																		icon={currencyIcon}
																		size={'md'}
																		color={
																			activeCase?.walletType ===
																			WalletType.Donate
																				? '#FFD035'
																				: '#ffffff'
																		}
																	/>
																	<Text style={{ fontWeight: 700 }}>
																		{totalPrice?.toLocaleString('ru-RU')}
																	</Text>
																</Fragment>
															)}
														</StyledButtonContent>
													</Button>
												</StyledButtonContainer>
											</StyledRightContainer>
										</StyledCaseContent>
									</CSSTransition>
								</SwitchTransition>
							</StyledInfoCase>
						)}
					</CSSTransition>
				</SwitchTransition>
			</StyledTopSection>
			<SwitchTransition>
				<CSSTransition
					timeout={600}
					classNames={`items-grid`}
					key={activeCase?.id || 'empty'}
					addEndListener={(node, done) =>
						node.addEventListener('transitionend', done, false)
					}
				>
					<StyledItemsGrid $dimmed={isOpeningCase}>
						{processedItems.map((item, index) => (
							<ShopOpenCaseItem
								key={index}
								type={item.type}
								id={item.ids}
								color={item.color}
								name={item.name}
							/>
						))}
					</StyledItemsGrid>
				</CSSTransition>
			</SwitchTransition>
		</StyledContainer>
	);
};

export { ShopOpenCase };
